import React, { useEffect } from 'react'
import { useT } from '../i18n'
import styles from './Drawer.module.css'

export type DrawerProps = React.PropsWithChildren<{
  open: boolean
  onClose: () => void
  title: string
  /** 'wide' (about 760 px) for detail panels such as a lot's tabs. */
  width?: 'default' | 'wide'
}>

export default function Drawer({ open, onClose, title, width = 'default', children }: DrawerProps) {
  const t = useT()

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        className={width === 'wide' ? `${styles.panel} ${styles.panelWide}` : styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('common.close')}
            title={t('common.close')}
          >
            ×
          </button>
        </div>
        <div className={styles.body}>{children}</div>
      </div>
    </div>
  )
}
